/** Shared CFO Insights presentation helpers. No network. No org-specific rules. */

export function fmtDollars(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const abs = Math.abs(Number(n));
  const sign = Number(n) < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Projected cash-out display. Null / source_missing must not render as $0.00.
 * Explicit numeric 0 still formats as $0.00.
 */
export function formatProjectedCashOut(value, status) {
  const missing =
    value == null || status === "source_missing";
  if (missing) {
    return {
      value: "—",
      hint: "AP due-date schedule not available",
    };
  }
  return {
    value: fmtDollars(value),
    hint: "AP obligations due within 30 days.",
  };
}

export function formatNetCashFlow(value) {
  return value != null ? fmtDollars(value) : "—";
}

/**
 * Payroll Bridge recommendation. Existing LOC headroom is considered before
 * recommending a new facility. Missing LOC is not treated as $0.
 */
export function recommendPayrollBridge({
  locAvailable,
  ar60,
  totalAr,
  ap,
  dcoh,
  wc,
  cr,
} = {}) {
  const arAtRisk =
    ar60 != null && totalAr != null && totalAr > 0 ? ar60 / totalAr : 0;
  const timingByAr = arAtRisk > 0.4;
  const timingByDcoh = dcoh != null && dcoh < 45;
  const timingByWc = wc != null && wc < 400_000;
  const timingNeedIndicated = timingByAr || timingByDcoh || timingByWc;

  let modeledNeedMax = null;
  let payrollCapacity;
  if (ar60 == null || totalAr == null) {
    payrollCapacity = "Capacity TBD — connect AR aging";
  } else {
    const exposure = ar60 + (ap ?? 0);
    if (exposure > 300_000) {
      payrollCapacity = "$200k–$350k range";
      modeledNeedMax = 350_000;
    } else if (exposure > 150_000) {
      payrollCapacity = "$100k–$200k range";
      modeledNeedMax = 200_000;
    } else {
      payrollCapacity = "$50k–$100k range";
      modeledNeedMax = 100_000;
    }
  }

  const locKnown = locAvailable != null && Number.isFinite(Number(locAvailable));
  const locMissing = !locKnown;

  if (!timingNeedIndicated) {
    return {
      payrollValue: "Not currently indicated",
      payrollCapacity,
      payrollTone: "good",
      payrollHint: `Current ratio ${cr != null ? Number(cr).toFixed(2) : "—"} and cash position support near-term payroll without a bridge.`,
      usesExistingLoc: false,
    };
  }

  if (locMissing) {
    return {
      payrollValue: "LOC headroom unknown",
      payrollCapacity,
      payrollTone: "neutral",
      payrollHint:
        "* Balance-sheet LOC available is not sourced — not assumed to be zero. Connect LOC data before recommending a new payroll bridge.",
      usesExistingLoc: false,
    };
  }

  const loc = Number(locAvailable);
  const coveredNeed = modeledNeedMax != null ? modeledNeedMax : 250_000;

  if (loc >= coveredNeed) {
    return {
      payrollValue: "Use existing LOC",
      payrollCapacity: `Existing ${fmtDollars(loc)} headroom covers modeled timing gap`,
      payrollTone: "good",
      payrollHint: `* Available LOC ${fmtDollars(loc)} covers the modeled payroll/timing need (up to ${fmtDollars(coveredNeed)}). Review the existing facility rather than adding a new payroll bridge. Illustrative, not lender approval.`,
      usesExistingLoc: true,
    };
  }

  if (loc > 0 && loc < coveredNeed) {
    const incremental = coveredNeed - loc;
    return {
      payrollValue: "Incremental capacity may be needed",
      payrollCapacity: `${fmtDollars(incremental)} above existing ${fmtDollars(loc)} LOC`,
      payrollTone: "warning",
      payrollHint: `* Modeled timing need exceeds available LOC. Existing headroom ${fmtDollars(loc)}; incremental gap about ${fmtDollars(incremental)}. Illustrative, not lender approval.`,
      usesExistingLoc: true,
    };
  }

  const payrollValue =
    timingByAr || timingByDcoh
      ? "$150k–$250k recommended"
      : "$100k–$200k recommended";
  const payrollTone = payrollValue.startsWith("$150k") ? "warning" : "neutral";
  const payrollHint = timingByAr
    ? `* AR aging: ${(arAtRisk * 100).toFixed(0)}% of receivables >60 days — timing gap risk.`
    : timingByDcoh
    ? `* DCOH of ${Math.round(dcoh)} days indicates limited cash runway buffer.`
    : `* Working capital below $400k — moderate timing buffer.`;

  return {
    payrollValue,
    payrollCapacity,
    payrollTone,
    payrollHint,
    usesExistingLoc: false,
  };
}
